/**
 * Platform-agnostic OneSignal wrapper for Chancey.
 *
 * Mirrors the shape of `purchasesService.ts` (native vs web dynamic import,
 * normalized public surface, feature-flagged off when the app id is unset).
 * The rest of the app only ever imports from this file; it never touches
 * the OneSignal SDKs directly.
 *
 * Web push support stays in place; we'll continue hardening browser delivery
 * later rather than deleting the work.
 *
 * Explicit non-goals:
 *   - Tag writes. OneSignal tags are a delivery-targeting concern, and the
 *     server is the only writer (see chancey-api/src/pushRoutes.ts). Adding
 *     `setTag` here would reintroduce the dual-writer race the plan set out
 *     to avoid. If a tag needs to change, call the API and let the server
 *     propagate.
 *   - Permission prompt on init. iOS App Review and UX research both say
 *     "never ask on launch". Permission requests are driven by explicit
 *     user actions — onboarding soft-ask + Settings toggle.
 */

import { Capacitor, registerPlugin } from '@capacitor/core';

type DeepLinkHandler = (route: string) => void;

type Platform = 'ios' | 'android' | 'web';
export type PushPermissionStatus =
  | 'not_determined'
  | 'denied'
  | 'authorized'
  | 'provisional'
  | 'ephemeral'
  | 'unknown';

const PLATFORM: Platform = ((): Platform => {
  const raw = Capacitor.getPlatform();
  return raw === 'ios' || raw === 'android' ? raw : 'web';
})();

const APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID ?? '';

interface IosDeliveredNotificationDebug {
  id?: string;
  title?: string;
  body?: string;
  date?: string;
}

export interface IosNotificationSettingsDebug {
  authorizationStatus?: PushPermissionStatus;
  authorizationStatusRaw?: number;
  alertSetting?: string;
  badgeSetting?: string;
  soundSetting?: string;
  lockScreenSetting?: string;
  notificationCenterSetting?: string;
  scheduledDeliverySetting?: string;
  timeSensitiveSetting?: string;
  criticalAlertSetting?: string;
  deliveredCount?: number;
  deliveredNotifications?: IosDeliveredNotificationDebug[];
}

interface NotificationDiagnosticsPlugin {
  getSettings(): Promise<IosNotificationSettingsDebug>;
}

const NotificationDiagnostics = registerPlugin<NotificationDiagnosticsPlugin>(
  'NotificationDiagnostics'
);

interface NativeSdk {
  kind: 'native';
  mod: typeof import('onesignal-cordova-plugin');
}
interface WebSdk {
  kind: 'web';
  /** The default export from `react-onesignal` is a singleton — store its type. */
  instance: typeof import('react-onesignal').default;
}

/**
 * Tracks whether the last `init()` attempt failed. If yes, subsequent calls
 * skip re-running the whole SDK load (which would just throw the same
 * uncaught error) and treat push as disabled for the session. A page
 * refresh is the recovery path — intentional, since most init failures
 * (origin mismatch, network issue, OneSignal config change) need one.
 */
let sdkPromise: Promise<NativeSdk | WebSdk> | null = null;
let initFailed = false;
let currentUserId: string | null = null;
let deepLinkHandler: DeepLinkHandler | null = null;

export interface ForegroundPushDebug {
  id?: string;
  title?: string;
  body?: string;
  route?: string;
  receivedAt: string;
}

export interface PushDebugState {
  configured: boolean;
  platform: Platform;
  appIdSuffix: string;
  initFailed: boolean;
  currentUserId: string | null;
  permission: boolean | null;
  permissionStatus?: PushPermissionStatus;
  permissionNative?: number | null;
  canRequestPermission?: boolean | null;
  iosSettings?: IosNotificationSettingsDebug | null;
  externalId?: string | null;
  onesignalId?: string | null;
  subscriptionId?: string | null;
  subscriptionTokenPresent?: boolean;
  optedIn?: boolean | null;
  lastForeground?: ForegroundPushDebug | null;
  error?: string;
}

let lastForeground: ForegroundPushDebug | null = null;
const foregroundListeners = new Set<(event: ForegroundPushDebug) => void>();

function normalizePushRoute(route: unknown): string {
  if (typeof route !== 'string') return '/home';
  const trimmed = route.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return '/home';
  return trimmed;
}

function appIdSuffix(): string {
  if (!APP_ID) return 'unset';
  return APP_ID.slice(-8);
}

function nativePermissionStatus(value: unknown): PushPermissionStatus {
  switch (value) {
    case 0:
      return 'not_determined';
    case 1:
      return 'denied';
    case 2:
      return 'authorized';
    case 3:
      return 'provisional';
    case 4:
      return 'ephemeral';
    default:
      return 'unknown';
  }
}

function webPermissionStatus(permission: NotificationPermission | boolean | undefined): PushPermissionStatus {
  if (permission === true || permission === 'granted') return 'authorized';
  if (permission === false || permission === 'default') return 'not_determined';
  if (permission === 'denied') return 'denied';
  return 'unknown';
}

export function hasBannerPermission(status: PushPermissionStatus): boolean {
  return status === 'authorized' || status === 'ephemeral';
}

function recordForegroundNotification(raw: {
  notificationId?: string;
  title?: string;
  body?: string;
  additionalData?: unknown;
}): void {
  const data = (raw.additionalData ?? {}) as { route?: string };
  const event: ForegroundPushDebug = {
    id: raw.notificationId,
    title: raw.title,
    body: raw.body,
    route: data.route,
    receivedAt: new Date().toISOString(),
  };
  lastForeground = event;
  foregroundListeners.forEach((listener) => {
    try {
      listener(event);
    } catch {
      /* observer errors should not affect push display */
    }
  });
}

export function subscribeToForegroundNotifications(
  listener: (event: ForegroundPushDebug) => void
): () => void {
  foregroundListeners.add(listener);
  return () => {
    foregroundListeners.delete(listener);
  };
}

/** Whether the push feature is enabled for this runtime. */
export function isPushConfigured(): boolean {
  return APP_ID.length > 0;
}

export function getPushPlatform(): Platform {
  return PLATFORM;
}

/**
 * Register a handler invoked when the user taps a notification. The handler
 * receives the route from `data.route` (server-set, see push.ts) so the app
 * can `history.push(route)` to deep-link.
 *
 * Register BEFORE calling `init()` so the initial tap event on cold-start
 * (native) doesn't drop on the floor.
 */
export function setDeepLinkHandler(handler: DeepLinkHandler | null): void {
  deepLinkHandler = handler;
}

async function loadSdk(): Promise<NativeSdk | WebSdk> {
  if (!APP_ID) {
    throw new Error('push: REACT_APP_ONESIGNAL_APP_ID is not set');
  }
  if (PLATFORM === 'ios' || PLATFORM === 'android') {
    const mod = await import('onesignal-cordova-plugin');
    // Initialize the native SDK. OneSignal v5 auto-prompts on iOS only if
    // we ask — we explicitly do not, so we bind the permission dialog to
    // onboarding soft-ask + Settings toggle.
    mod.default.initialize(APP_ID);
    // Record foreground deliveries for Admin diagnostics. Do not call
    // preventDefault/display here: the Cordova wrapper automatically
    // proceeds with foreground display after listeners run.
    mod.default.Notifications.addEventListener('foregroundWillDisplay', (event) => {
      recordForegroundNotification(event.getNotification());
    });
    mod.default.Notifications.addEventListener('click', (event) => {
      try {
        const data = event?.notification?.additionalData as
          | { route?: string }
          | undefined;
        const route = normalizePushRoute(data?.route);
        if (deepLinkHandler) deepLinkHandler(route);
      } catch {
        /* swallow: a misbehaving SDK payload must not crash the app */
      }
    });
    return { kind: 'native', mod };
  }

  // Web path. `react-onesignal` calls OneSignal's global web SDK under the
  // hood. `allowLocalhostAsSecureOrigin` lets the dev server (http) trigger
  // the web push flow — in production over HTTPS, the flag is a no-op.
  //
  // We intentionally omit `autoRegister: false` — it's legacy v15 and
  // confuses v16 init behaviour. `autoResubscribe: true` is the right
  // default: if the user previously opted in, keep them subscribed
  // automatically; we only handle the FIRST opt-in explicitly via
  // requestPermission().
  const mod = await import('react-onesignal');
  const instance = mod.default;

  // Cloudflare cache-poisoning workaround.
  //
  // OneSignal's v16 SDK fetches its web config via JSONP at
  //   https://api.onesignal.com/sync/<app_id>/web?callback=__jp0
  // Its JSONP library hardcodes `__jp` as the callback prefix and uses a
  // per-page-load counter, so the FIRST call on every fresh page is always
  // `callback=__jp0`. Cloudflare's edge caches responses keyed by the full
  // URL (including query string) with `max-age=3600`. If the app owner
  // updates the site origin in the OneSignal dashboard between the time
  // Cloudflare first cached `callback=__jp0` and when the TTL expires,
  // every subsequent user's first SDK init receives the OLD config for up
  // to an hour — producing `Error: Can only be used on: <old origin>` at
  // page load and no way to recover short of waiting out the TTL.
  //
  // Fix: intercept the <script src="…?callback=__jp0"> the SDK injects,
  // rewrite the callback name to a freshly-random string (cache miss →
  // fresh origin fetch), and alias `window[freshName] = window.__jp0` so
  // the JSONP response still resolves the SDK's waiting Promise.
  //
  // Idempotent — we tag the window with a flag so HMR / re-init doesn't
  // stack multiple interceptors.
  const scriptDescriptor = Object.getOwnPropertyDescriptor(
    HTMLScriptElement.prototype,
    'src'
  );
  const hookFlag = '__chanceyOsScriptHooked' as const;
  if (
    scriptDescriptor?.set &&
    !(window as unknown as Record<string, unknown>)[hookFlag]
  ) {
    const originalSetter = scriptDescriptor.set;
    Object.defineProperty(HTMLScriptElement.prototype, 'src', {
      configurable: true,
      enumerable: scriptDescriptor.enumerable,
      get: scriptDescriptor.get,
      set(value: string) {
        let effective = value;
        if (typeof value === 'string' && /api\.onesignal\.com\/sync\/.*[?&]callback=__jp/.test(value)) {
          try {
            const u = new URL(value);
            const originalCallback = u.searchParams.get('callback');
            if (originalCallback) {
              // Random enough to dodge any accumulated CF cache entries.
              const freshCallback =
                `chanceyCb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
              u.searchParams.set('callback', freshCallback);
              effective = u.toString();
              // Wait for the SDK to create window[originalCallback], then
              // alias it onto window[freshCallback] so the JSONP response
              // (which invokes window[freshCallback]) reaches the SDK.
              // Polling rather than sync-checking because the lib may
              // create the callback after the src setter returns.
              const w = window as unknown as Record<string, unknown>;
              const start = Date.now();
              const alias = () => {
                if (typeof w[originalCallback] === 'function') {
                  w[freshCallback] = w[originalCallback];
                } else if (Date.now() - start < 2000) {
                  setTimeout(alias, 10);
                } else {
                  // Leave a real failure signal — the SDK will hang waiting
                  // for its JSONP callback if we can't wire this up.
                  // eslint-disable-next-line no-console
                  console.error(
                    '[push] Cache-bypass alias timed out; JSONP may fail to resolve'
                  );
                }
              };
              alias();
            }
          } catch {
            // If URL parsing fails for any reason, fall through with the
            // original URL so the SDK at least has a chance.
            effective = value;
          }
        }
        originalSetter.call(this, effective);
      },
    });
    (window as unknown as Record<string, unknown>)[hookFlag] = true;
  }

  try {
    await instance.init({
      appId: APP_ID,
      allowLocalhostAsSecureOrigin:
        (process.env.NODE_ENV ?? 'development') !== 'production',
      autoResubscribe: true,
    } as Parameters<typeof instance.init>[0]);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[push] OneSignal init failed', err);
    throw err;
  }
  instance.Notifications.addEventListener('click', (event) => {
    try {
      const data = (event?.notification?.additionalData ?? {}) as {
        route?: string;
      };
      const route = normalizePushRoute(data.route);
      if (deepLinkHandler) deepLinkHandler(route);
    } catch {
      /* swallow */
    }
  });
  instance.Notifications.addEventListener('foregroundWillDisplay', (event) => {
    recordForegroundNotification(event.notification);
  });
  return { kind: 'web', instance };
}

/**
 * Idempotent init. Safe to call from React effects that may fire multiple
 * times; the underlying SDK is only booted once.
 *
 * Does NOT prompt for permission or set External ID. Both are explicit
 * follow-up calls (`requestPermission`, `login`) to keep this function's
 * side-effects contained.
 *
 * Never throws into the caller's promise chain. Init failures (origin
 * mismatch, offline, OneSignal outage) are logged via `console.error` but
 * swallowed so unhandled-rejection errors don't surface in React's dev
 * overlay. `isPushConfigured()` remains `true` on failure because the
 * feature flag itself hasn't changed — callers should treat failures as
 * "push unavailable for this session, try again on next page load".
 */
export async function init(): Promise<void> {
  if (!isPushConfigured()) return;
  if (initFailed) return; // already failed this session — don't retry
  if (!sdkPromise) {
    sdkPromise = loadSdk().catch((err) => {
      initFailed = true;
      // Rethrow so the same error reaches anyone who was awaiting this
      // promise, but we'll trap it in the outer catch below.
      throw err;
    });
  }
  try {
    await sdkPromise;
  } catch {
    // Already logged inside loadSdk's catch. Swallowing the re-throw stops
    // the uncaught-rejection noise in DevTools while debugging.
    sdkPromise = null;
  }
}

/**
 * Identify the current user for push routing.
 *
 * Native SDK login is reliable and gives OneSignal the Clerk id directly.
 * The Web SDK login path can leave Chrome subscriptions in a non-delivering
 * state after an external-id merge, so web identity is owned by the Worker:
 * the client reports its subscription id with prefs/test calls, and the
 * Worker transfers that subscription + writes routing tags server-side.
 */
export async function login(userId: string): Promise<void> {
  if (!isPushConfigured()) return;
  if (!userId) return;
  if (currentUserId === userId) return;
  await init();
  const sdk = await sdkPromise;
  if (!sdk) return;
  if (sdk.kind === 'native') {
    sdk.mod.default.login(userId);
  } else {
    try {
      if (sdk.instance.User?.externalId) await sdk.instance.logout();
    } catch {
      /* best-effort: server-side subscription ownership still handles routing */
    }
  }
  currentUserId = userId;
}

export async function logout(): Promise<void> {
  if (!sdkPromise) return;
  const sdk = await sdkPromise;
  try {
    if (sdk.kind === 'native') {
      sdk.mod.default.logout();
    } else {
      await sdk.instance.logout();
    }
  } catch {
    /* swallow: logging out a user who was never identified is fine */
  }
  currentUserId = null;
}

export interface PermissionResult {
  granted: boolean;
  /** True when the SDK was never initialised (push feature flag off). */
  notConfigured?: boolean;
}

/**
 * Prompt the OS for permission to send notifications. Only call from an
 * explicit user action (button tap in onboarding or Settings). iOS will
 * only show the dialog ONCE per install; after that, `granted` reflects
 * the cached decision and the user must change it in system settings.
 *
 * On web, we defer to the browser's permission model. Chromium and Firefox
 * require a user gesture (this function assumes it is called from one).
 *
 * Critical web subtlety: `Notifications.requestPermission()` only prompts
 * the browser; it does NOT create a OneSignal subscription. We have to
 * explicitly call `User.PushSubscription.optIn()` afterwards or the user
 * grants permission and still shows as "0 subscribers" in OneSignal.
 */
export async function requestPermission(): Promise<PermissionResult> {
  if (!isPushConfigured()) return { granted: false, notConfigured: true };
  await init();
  const sdk = await sdkPromise;
  if (!sdk) return { granted: false, notConfigured: true };
  try {
    if (sdk.kind === 'native') {
      const granted = await sdk.mod.default.Notifications.requestPermission(true);
      return { granted: !!granted };
    }
    // Web: prompt, then opt into the push subscription if granted.
    const granted = await sdk.instance.Notifications.requestPermission();
    if (granted) {
      try {
        await sdk.instance.User.PushSubscription.optIn();
      } catch (optErr) {
        // eslint-disable-next-line no-console
        console.error('[push] web optIn failed', optErr);
      }
    }
    return { granted: !!granted };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[push] requestPermission threw', err);
    return { granted: false };
  }
}

export async function hasPermission(): Promise<boolean> {
  if (!isPushConfigured()) return false;
  if (!sdkPromise) return false;
  const sdk = await sdkPromise;
  try {
    if (sdk.kind === 'native') {
      // Prefer the async getter; the sync `hasPermission()` is deprecated
      // and reports a cached value that can be stale on first app launch.
      const has = await sdk.mod.default.Notifications.getPermissionAsync();
      return !!has;
    }
    return !!sdk.instance.Notifications.permission;
  } catch {
    return false;
  }
}

export async function getPermissionStatus(): Promise<PushPermissionStatus> {
  if (!isPushConfigured()) return 'unknown';
  await init();
  const sdk = sdkPromise ? await sdkPromise : null;
  if (!sdk) return 'unknown';
  try {
    if (sdk.kind === 'native') {
      return nativePermissionStatus(
        await sdk.mod.default.Notifications.permissionNative()
      );
    }
    if (typeof Notification !== 'undefined') {
      return webPermissionStatus(Notification.permission);
    }
    return webPermissionStatus(sdk.instance.Notifications.permission);
  } catch {
    return 'unknown';
  }
}

function settledValue<T>(result: PromiseSettledResult<T>): T | undefined {
  return result.status === 'fulfilled' ? result.value : undefined;
}

export async function getPushDebugState(): Promise<PushDebugState> {
  const base: PushDebugState = {
    configured: isPushConfigured(),
    platform: PLATFORM,
    appIdSuffix: appIdSuffix(),
    initFailed,
    currentUserId,
    permission: null,
    lastForeground,
  };
  if (!isPushConfigured()) return base;

  try {
    await init();
    const sdk = sdkPromise ? await sdkPromise : null;
    if (!sdk) {
      return { ...base, initFailed, error: initFailed ? 'init_failed' : 'sdk_not_loaded' };
    }

    if (sdk.kind === 'native') {
      const os = sdk.mod.default;
      const [
        permission,
        permissionNative,
        canRequestPermission,
        iosSettings,
        externalId,
        onesignalId,
        subscriptionId,
        token,
        optedIn,
      ] =
        await Promise.allSettled([
          os.Notifications.getPermissionAsync(),
          os.Notifications.permissionNative(),
          os.Notifications.canRequestPermission(),
          PLATFORM === 'ios'
            ? NotificationDiagnostics.getSettings()
            : Promise.resolve(null),
          os.User.getExternalId(),
          os.User.getOnesignalId(),
          os.User.pushSubscription.getIdAsync(),
          os.User.pushSubscription.getTokenAsync(),
          os.User.pushSubscription.getOptedInAsync(),
        ]);
      const pushToken = settledValue(token);
      const nativePermission = settledValue(permissionNative);
      return {
        ...base,
        initFailed,
        permission: !!settledValue(permission),
        permissionStatus: nativePermissionStatus(nativePermission),
        permissionNative: typeof nativePermission === 'number' ? nativePermission : null,
        canRequestPermission: settledValue(canRequestPermission) ?? null,
        iosSettings: settledValue(iosSettings) ?? null,
        externalId: settledValue(externalId) ?? null,
        onesignalId: settledValue(onesignalId) ?? null,
        subscriptionId: settledValue(subscriptionId) ?? null,
        subscriptionTokenPresent: typeof pushToken === 'string' && pushToken.length > 0,
        optedIn: settledValue(optedIn) ?? null,
        lastForeground,
      };
    }

    const sub = sdk.instance.User.PushSubscription;
    const permissionStatus =
      typeof Notification !== 'undefined'
        ? webPermissionStatus(Notification.permission)
        : webPermissionStatus(sdk.instance.Notifications.permission);
    return {
      ...base,
      initFailed,
      permission: !!sdk.instance.Notifications.permission,
      permissionStatus,
      permissionNative: null,
      canRequestPermission: permissionStatus === 'not_determined',
      externalId: sdk.instance.User.externalId ?? null,
      onesignalId: sdk.instance.User.onesignalId ?? null,
      subscriptionId: sub.id ?? null,
      subscriptionTokenPresent: typeof sub.token === 'string' && sub.token.length > 0,
      optedIn: sub.optedIn ?? null,
      lastForeground,
    };
  } catch (err) {
    return {
      ...base,
      initFailed,
      error: err instanceof Error ? err.message : String(err),
      lastForeground,
    };
  }
}
